import { persistGlobalNroSuc, readGlobalNroSuc } from '../utils/nroSucPreferida';
import ajusteInventarioService from './ajusteInventarioService';
import api from './api';

export interface GranjaLoteConfig {
  codigo: string;
  articuloAves: string;
  articuloHuevoBruto: string;
  fechaIngreso?: string | null;
  cabezasIniciales: number;
  toleranciaPct: number;
  activo: boolean;
}

export interface GranjaArticuloClasificacion {
  calibre: string;
  color: string;
  codigoArticulo: string;
}

export interface GranjaConfig {
  depositoGranja?: number | null;
  lotes: GranjaLoteConfig[];
  articulosClasificacion: GranjaArticuloClasificacion[];
  articuloCascado?: string | null;
  recetaMixerDefault?: string | null;
  batchKgDefault?: number | null;
}

export interface GranjaClasificacionItem {
  codigoArticulo: string;
  cantidad: number;
  calibre?: string;
  color?: string;
}

export interface GranjaClasificacionRequest {
  codigoLote?: string;
  codigoArticuloBruto?: string;
  cantidadBruta: number;
  vendibles: GranjaClasificacionItem[];
  cantidadCascado: number;
  codigoArticuloCascado?: string | null;
  cantidadTirado: number;
  nroUbicacion?: number | null;
  nroSuc: number;
  observaciones?: string;
  fecha?: string;
}

export type GranjaMovimientoTipo =
  | 'RECOLECCION'
  | 'CLASIFICACION'
  | 'MORTANDAD'
  | 'DESCARTE'
  | 'ALTA_LOTE'
  | 'DESPACHO_PROD';

export interface GranjaMovimientoListItem {
  nroFormulario: number;
  nroComprobante: number;
  letra: string;
  fechaEmision?: string | null;
  codigoMotivo: string;
  motivoLabel: string;
  descripcion: string;
  codigosLote?: string | null;
  nroSuc: number;
  importeTotal?: number | null;
  nroUbicacionOrigen: number;
  nroUbicacionDestino: number;
  nroTipoFormulario: string;
}

export interface GranjaMovimientoPagedResult {
  items: GranjaMovimientoListItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface GranjaMovimientoSearchParams {
  fechaDesde?: string;
  fechaHasta?: string;
  tipo?: GranjaMovimientoTipo | string;
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface GranjaClasificacionResult {
  nroFormularioEgreso: number;
  nroFormularioIngreso: number;
  cantidadBruta: number;
  cantidadVendible: number;
  cantidadCascado: number;
  cantidadTirado: number;
  advertencia?: string | null;
}

export interface GranjaLoteKpi {
  codigoLote: string;
  articuloAves: string;
  articuloHuevoBruto: string;
  cabezasVivas: number;
  cabezasIniciales: number;
  toleranciaPct: number;
  fechaIngreso?: string | null;
  huevosRecolectados: number;
  porcentajePostura: number;
  bajasDia: number;
  mortalidadAcumuladaPct: number;
  alertaSanitaria: boolean;
}

export interface GranjaCalibreRatio {
  calibre: string;
  color: string;
  codigoArticulo: string;
  cantidad: number;
  porcentaje: number;
}

export interface GranjaCalibreCantidadItem {
  calibre: string;
  color: string;
  codigoArticulo: string;
  cantidad: number;
}

export interface GranjaClasificacionDia {
  fecha: string;
  items: GranjaCalibreCantidadItem[];
}

export interface GranjaPosturaMortandadLote {
  codigoLote: string;
  huevos: number;
  mortandad: number;
  cabezasVivas: number;
}

export interface GranjaPosturaMortandadDia {
  fecha: string;
  huevos: number;
  mortandad: number;
  lotes: GranjaPosturaMortandadLote[];
}

export interface GranjaAutonomiaMp {
  codigoArticulo: string;
  descripcion?: string | null;
  stockDisponible: number;
  consumoDiarioPromedio: number;
  diasAutonomia: number;
  stockMinimo?: number | null;
  alertaStockMinimo: boolean;
}

export interface GranjaKpis {
  desde: string;
  hasta: string;
  lotes: GranjaLoteKpi[];
  ratioCalibres: GranjaCalibreRatio[];
  clasificacionUltimos7Dias: GranjaClasificacionDia[];
  posturaMortandadUltimos7Dias: GranjaPosturaMortandadDia[];
  indiceRoturaPct: number;
  totalRecolectado: number;
  totalCascado: number;
  totalTirado: number;
  autonomiaAlimento: GranjaAutonomiaMp[];
}

export interface GranjaRegistroLoteRequest {
  codigoLote: string;
  cantidad: number;
  nroSuc: number;
  fecha?: string;
  observaciones?: string;
}

export interface GranjaBajaRequest extends GranjaRegistroLoteRequest {
  tipo: 'MORTANDAD' | 'DESCARTE';
}

export interface GranjaRegistroLoteResult {
  id: number;
  nroFormulario: number;
  cantidad: number;
}

export interface GranjaInformePosturaLote {
  codigoLote: string;
  cantidadPostura: number;
  semanasProductivas: number;
  muertas: number;
  cantidadGallinas: number;
  porcentajePostura: number;
}

export interface GranjaInformePosturaTotal {
  cantidadPostura: number;
  muertas: number;
  cantidadGallinas: number;
  porcentajePostura: number;
}

export interface GranjaInformePosturaSemanaFila {
  fechaInicial: string;
  fechaFinal: string;
  dias: number;
  lotes: GranjaInformePosturaLote[];
  total: GranjaInformePosturaTotal;
}

export interface GranjaInformePosturaSemanal {
  desde: string;
  hasta: string;
  codigosLote: string[];
  filas: GranjaInformePosturaSemanaFila[];
}

const asObj = (raw: unknown): Record<string, unknown> =>
  raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};

const normalizeLote = (raw: Record<string, unknown>): GranjaLoteConfig => ({
  codigo: String(raw.codigo ?? raw.Codigo ?? ''),
  articuloAves: String(raw.articuloAves ?? raw.ArticuloAves ?? ''),
  articuloHuevoBruto: String(raw.articuloHuevoBruto ?? raw.ArticuloHuevoBruto ?? ''),
  fechaIngreso: (raw.fechaIngreso ?? raw.FechaIngreso ?? null) as string | null,
  cabezasIniciales: Number(raw.cabezasIniciales ?? raw.CabezasIniciales ?? 0),
  toleranciaPct: Number(raw.toleranciaPct ?? raw.ToleranciaPct ?? 0),
  activo: Boolean(raw.activo ?? raw.Activo ?? true),
});

const normalizeConfig = (raw: unknown): GranjaConfig => {
  const o = asObj(raw);
  const lotesRaw = (o.lotes ?? o.Lotes ?? []) as unknown[];
  const artsRaw = (o.articulosClasificacion ?? o.ArticulosClasificacion ?? []) as unknown[];
  return {
    depositoGranja: o.depositoGranja != null || o.DepositoGranja != null
      ? Number(o.depositoGranja ?? o.DepositoGranja)
      : null,
    lotes: lotesRaw.map((l) => normalizeLote(asObj(l))),
    articulosClasificacion: artsRaw.map((a) => {
      const r = asObj(a);
      return {
        calibre: String(r.calibre ?? r.Calibre ?? ''),
        color: String(r.color ?? r.Color ?? ''),
        codigoArticulo: String(r.codigoArticulo ?? r.CodigoArticulo ?? ''),
      };
    }),
    articuloCascado: (o.articuloCascado ?? o.ArticuloCascado ?? null) as string | null,
    recetaMixerDefault: (o.recetaMixerDefault ?? o.RecetaMixerDefault ?? null) as string | null,
    batchKgDefault: o.batchKgDefault != null || o.BatchKgDefault != null
      ? Number(o.batchKgDefault ?? o.BatchKgDefault)
      : null,
  };
};

const normalizeKpis = (raw: unknown): GranjaKpis => {
  const o = asObj(raw);
  return {
    desde: String(o.desde ?? o.Desde ?? ''),
    hasta: String(o.hasta ?? o.Hasta ?? ''),
    lotes: ((o.lotes ?? o.Lotes ?? []) as unknown[]).map((l) => {
      const r = asObj(l);
      return {
        codigoLote: String(r.codigoLote ?? r.CodigoLote ?? ''),
        articuloAves: String(r.articuloAves ?? r.ArticuloAves ?? ''),
        articuloHuevoBruto: String(r.articuloHuevoBruto ?? r.ArticuloHuevoBruto ?? ''),
        cabezasVivas: Number(r.cabezasVivas ?? r.CabezasVivas ?? 0),
        cabezasIniciales: Number(r.cabezasIniciales ?? r.CabezasIniciales ?? 0),
        toleranciaPct: Number(r.toleranciaPct ?? r.ToleranciaPct ?? 0),
        fechaIngreso: (r.fechaIngreso ?? r.FechaIngreso ?? null) as string | null,
        huevosRecolectados: Number(r.huevosRecolectados ?? r.HuevosRecolectados ?? 0),
        porcentajePostura: Number(r.porcentajePostura ?? r.PorcentajePostura ?? 0),
        bajasDia: Number(r.bajasDia ?? r.BajasDia ?? 0),
        mortalidadAcumuladaPct: Number(r.mortalidadAcumuladaPct ?? r.MortalidadAcumuladaPct ?? 0),
        alertaSanitaria: Boolean(r.alertaSanitaria ?? r.AlertaSanitaria ?? false),
      };
    }),
    ratioCalibres: ((o.ratioCalibres ?? o.RatioCalibres ?? []) as unknown[]).map((a) => {
      const r = asObj(a);
      return {
        calibre: String(r.calibre ?? r.Calibre ?? ''),
        color: String(r.color ?? r.Color ?? ''),
        codigoArticulo: String(r.codigoArticulo ?? r.CodigoArticulo ?? ''),
        cantidad: Number(r.cantidad ?? r.Cantidad ?? 0),
        porcentaje: Number(r.porcentaje ?? r.Porcentaje ?? 0),
      };
    }),
    clasificacionUltimos7Dias: (
      (o.clasificacionUltimos7Dias ?? o.ClasificacionUltimos7Dias ?? []) as unknown[]
    ).map((d) => {
      const r = asObj(d);
      const itemsRaw = (r.items ?? r.Items ?? []) as unknown[];
      return {
        fecha: String(r.fecha ?? r.Fecha ?? ''),
        items: itemsRaw.map((it) => {
          const i = asObj(it);
          return {
            calibre: String(i.calibre ?? i.Calibre ?? ''),
            color: String(i.color ?? i.Color ?? ''),
            codigoArticulo: String(i.codigoArticulo ?? i.CodigoArticulo ?? ''),
            cantidad: Number(i.cantidad ?? i.Cantidad ?? 0),
          };
        }),
      };
    }),
    posturaMortandadUltimos7Dias: (
      (o.posturaMortandadUltimos7Dias ?? o.PosturaMortandadUltimos7Dias ?? []) as unknown[]
    ).map((d) => {
      const r = asObj(d);
      const lotesRaw = (r.lotes ?? r.Lotes ?? []) as unknown[];
      return {
        fecha: String(r.fecha ?? r.Fecha ?? ''),
        huevos: Number(r.huevos ?? r.Huevos ?? 0),
        mortandad: Number(r.mortandad ?? r.Mortandad ?? 0),
        lotes: lotesRaw.map((l) => {
          const i = asObj(l);
          return {
            codigoLote: String(i.codigoLote ?? i.CodigoLote ?? ''),
            huevos: Number(i.huevos ?? i.Huevos ?? 0),
            mortandad: Number(i.mortandad ?? i.Mortandad ?? 0),
            cabezasVivas: Number(i.cabezasVivas ?? i.CabezasVivas ?? 0),
          };
        }),
      };
    }),
    indiceRoturaPct: Number(o.indiceRoturaPct ?? o.IndiceRoturaPct ?? 0),
    totalRecolectado: Number(o.totalRecolectado ?? o.TotalRecolectado ?? 0),
    totalCascado: Number(o.totalCascado ?? o.TotalCascado ?? 0),
    totalTirado: Number(o.totalTirado ?? o.TotalTirado ?? 0),
    autonomiaAlimento: ((o.autonomiaAlimento ?? o.AutonomiaAlimento ?? []) as unknown[]).map((a) => {
      const r = asObj(a);
      return {
        codigoArticulo: String(r.codigoArticulo ?? r.CodigoArticulo ?? ''),
        descripcion: (r.descripcion ?? r.Descripcion ?? null) as string | null,
        stockDisponible: Number(r.stockDisponible ?? r.StockDisponible ?? 0),
        consumoDiarioPromedio: Number(r.consumoDiarioPromedio ?? r.ConsumoDiarioPromedio ?? 0),
        diasAutonomia: Number(r.diasAutonomia ?? r.DiasAutonomia ?? 0),
        stockMinimo: r.stockMinimo != null || r.StockMinimo != null
          ? Number(r.stockMinimo ?? r.StockMinimo)
          : null,
        alertaStockMinimo: Boolean(r.alertaStockMinimo ?? r.AlertaStockMinimo ?? false),
      };
    }),
  };
};

/** Sucursal operativa: preferida del usuario → localStorage. Sin fallback inventado. */
export function resolveNroSuc(preferidaNro?: number | null): number {
  const preferida = Number(preferidaNro);
  if (Number.isFinite(preferida) && preferida > 0) return preferida;
  return readGlobalNroSuc() ?? 0;
}

/** Resuelve sucursal; si falta, toma la del próximo número de ajuste (como Inventario). */
export async function resolveNroSucOperativo(preferidaNro?: number | null): Promise<number> {
  const sync = resolveNroSuc(preferidaNro);
  if (sync > 0) {
    persistGlobalNroSuc(sync);
    return sync;
  }
  try {
    const next = await ajusteInventarioService.getNextNroFormulario('positivo');
    if (next.nroSuc && next.nroSuc > 0) {
      persistGlobalNroSuc(next.nroSuc);
      return next.nroSuc;
    }
  } catch {
    /* ignore */
  }
  return 0;
}

const granjaService = {
  getConfig: async (): Promise<GranjaConfig> => {
    const resp = await api.get('/api/Granja/config');
    return normalizeConfig(resp.data);
  },

  clasificar: async (dto: GranjaClasificacionRequest): Promise<GranjaClasificacionResult> => {
    const resp = await api.post('/api/Granja/clasificaciones', dto);
    const o = asObj(resp.data);
    return {
      nroFormularioEgreso: Number(o.nroFormularioEgreso ?? o.NroFormularioEgreso ?? 0),
      nroFormularioIngreso: Number(o.nroFormularioIngreso ?? o.NroFormularioIngreso ?? 0),
      cantidadBruta: Number(o.cantidadBruta ?? o.CantidadBruta ?? 0),
      cantidadVendible: Number(o.cantidadVendible ?? o.CantidadVendible ?? 0),
      cantidadCascado: Number(o.cantidadCascado ?? o.CantidadCascado ?? 0),
      cantidadTirado: Number(o.cantidadTirado ?? o.CantidadTirado ?? 0),
    };
  },

  getKpis: async (desde?: string, hasta?: string): Promise<GranjaKpis> => {
    const resp = await api.get('/api/Granja/kpis', {
      params: { desde, hasta },
    });
    return normalizeKpis(resp.data);
  },

  registrarRecoleccion: async (
    dto: GranjaRegistroLoteRequest,
  ): Promise<GranjaRegistroLoteResult> => {
    const resp = await api.post('/api/Granja/recolecciones', dto);
    const o = asObj(resp.data);
    return {
      id: Number(o.id ?? o.Id ?? 0),
      nroFormulario: Number(o.nroFormulario ?? o.NroFormulario ?? 0),
      cantidad: Number(o.cantidad ?? o.Cantidad ?? 0),
    };
  },

  registrarBaja: async (dto: GranjaBajaRequest): Promise<GranjaRegistroLoteResult> => {
    const resp = await api.post('/api/Granja/bajas', dto);
    const o = asObj(resp.data);
    return {
      id: Number(o.id ?? o.Id ?? 0),
      nroFormulario: Number(o.nroFormulario ?? o.NroFormulario ?? 0),
      cantidad: Number(o.cantidad ?? o.Cantidad ?? 0),
    };
  },

  getMovimientos: async (
    params: GranjaMovimientoSearchParams = {},
  ): Promise<GranjaMovimientoPagedResult> => {
    const resp = await api.get('/api/Granja/movimientos', {
      params: {
        FechaDesde: params.fechaDesde,
        FechaHasta: params.fechaHasta,
        Tipo: params.tipo,
        SearchTerm: params.searchTerm,
        PageNumber: params.pageNumber ?? 1,
        PageSize: params.pageSize ?? 20,
      },
    });
    const o = asObj(resp.data);
    const items = ((o.items ?? o.Items ?? []) as unknown[]).map((raw) => {
      const r = asObj(raw);
      return {
        nroFormulario: Number(r.nroFormulario ?? r.NroFormulario ?? 0),
        nroComprobante: Number(r.nroComprobante ?? r.NroComprobante ?? 0),
        letra: String(r.letra ?? r.Letra ?? 'X'),
        fechaEmision: (r.fechaEmision ?? r.FechaEmision ?? null) as string | null,
        codigoMotivo: String(r.codigoMotivo ?? r.CodigoMotivo ?? ''),
        motivoLabel: String(r.motivoLabel ?? r.MotivoLabel ?? ''),
        descripcion: String(r.descripcion ?? r.Descripcion ?? ''),
        codigosLote: (r.codigosLote ?? r.CodigosLote ?? null) as string | null,
        nroSuc: Number(r.nroSuc ?? r.NroSuc ?? 0),
        importeTotal: (r.importeTotal ?? r.ImporteTotal ?? null) as number | null,
        nroUbicacionOrigen: Number(r.nroUbicacionOrigen ?? r.NroUbicacionOrigen ?? 0),
        nroUbicacionDestino: Number(r.nroUbicacionDestino ?? r.NroUbicacionDestino ?? 0),
        nroTipoFormulario: String(r.nroTipoFormulario ?? r.NroTipoFormulario ?? ''),
      } satisfies GranjaMovimientoListItem;
    });
    return {
      items,
      totalCount: Number(o.totalCount ?? o.TotalCount ?? 0),
      pageNumber: Number(o.pageNumber ?? o.PageNumber ?? 1),
      pageSize: Number(o.pageSize ?? o.PageSize ?? 20),
    };
  },

  getInformePosturaSemanal: async (params: {
    desde: string;
    hasta: string;
    lotes?: string[];
  }): Promise<GranjaInformePosturaSemanal> => {
    const resp = await api.get('/api/Granja/informes/postura-semanal', {
      params: {
        desde: params.desde,
        hasta: params.hasta,
        lotes: params.lotes?.length ? params.lotes.join(',') : undefined,
      },
    });
    const o = asObj(resp.data);
    return {
      desde: String(o.desde ?? o.Desde ?? ''),
      hasta: String(o.hasta ?? o.Hasta ?? ''),
      codigosLote: ((o.codigosLote ?? o.CodigosLote ?? []) as unknown[]).map((c) => String(c)),
      filas: ((o.filas ?? o.Filas ?? []) as unknown[]).map((f) => {
        const r = asObj(f);
        const totalRaw = asObj(r.total ?? r.Total);
        return {
          fechaInicial: String(r.fechaInicial ?? r.FechaInicial ?? ''),
          fechaFinal: String(r.fechaFinal ?? r.FechaFinal ?? ''),
          dias: Number(r.dias ?? r.Dias ?? 0),
          lotes: ((r.lotes ?? r.Lotes ?? []) as unknown[]).map((l) => {
            const x = asObj(l);
            return {
              codigoLote: String(x.codigoLote ?? x.CodigoLote ?? ''),
              cantidadPostura: Number(x.cantidadPostura ?? x.CantidadPostura ?? 0),
              semanasProductivas: Number(x.semanasProductivas ?? x.SemanasProductivas ?? 0),
              muertas: Number(x.muertas ?? x.Muertas ?? 0),
              cantidadGallinas: Number(x.cantidadGallinas ?? x.CantidadGallinas ?? 0),
              porcentajePostura: Number(x.porcentajePostura ?? x.PorcentajePostura ?? 0),
            };
          }),
          total: {
            cantidadPostura: Number(totalRaw.cantidadPostura ?? totalRaw.CantidadPostura ?? 0),
            muertas: Number(totalRaw.muertas ?? totalRaw.Muertas ?? 0),
            cantidadGallinas: Number(totalRaw.cantidadGallinas ?? totalRaw.CantidadGallinas ?? 0),
            porcentajePostura: Number(totalRaw.porcentajePostura ?? totalRaw.PorcentajePostura ?? 0),
          },
        };
      }),
    };
  },
};

export default granjaService;
